<template>
  <div class="login-container">
    <div class="login-shell">
      <section class="login-showcase">
        <div class="showcase-content">
          <div class="showcase-brand">
            <span class="showcase-mark"><i class="el-icon-reading" aria-hidden="true"></i></span>
            <span>学途无忧 · 管理平台</span>
          </div>
          <div class="showcase-heading">
            <h1>{{ systemTitle }}</h1>
            <p class="showcase-overline">让每一步管理，都更从容。</p>
          </div>
          <el-form
            ref="loginForm"
            :model="form"
            :rules="formRules"
            label-position="top"
            class="login-form"
            @submit.native.prevent="handleSubmit"
          >
            <el-form-item label="账号或手机号" prop="phone">
              <el-input
                v-model="form.phone"
                placeholder="请输入账号或手机号"
                autocomplete="off"
                prefix-icon="el-icon-user"
                size="large"
                clearable
              />
            </el-form-item>

            <el-form-item label="登录密码" prop="password">
              <el-input
                v-model="form.password"
                type="password"
                placeholder="请输入登录密码"
                autocomplete="off"
                show-password
                prefix-icon="el-icon-lock"
                size="large"
                clearable
                @keyup.enter.native="handleSubmit"
              />
            </el-form-item>

            <el-form-item class="login-form__action">
              <el-button
                type="primary"
                native-type="button"
                :loading="loading"
                class="login-btn"
                @click="handleSubmit"
              >
                <span v-if="!loading">登录</span>
                <span v-else>正在登录...</span>
              </el-button>
            </el-form-item>

            <p v-if="message" class="msg">{{ message }}</p>
          </el-form>
          <div class="footer-content">
            <div class="footer-meta">
              <span>津ICP备2026013963号</span>
              <a href="https://www.12377.cn/" target="_blank" class="footer-link report-link">网上有害信息举报专区</a>
            </div>
            <span class="footer-copyright">
              Copyright © 2024 - 2026 XueTuWuYou. All Rights Reserved. 天津学途无忧科技有限公司 版权所有
            </span>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import lottie from 'lottie-web';
import { strFromU8, unzipSync } from 'fflate';
import botFramerLottieArchive from '@/assets/Cute Interactive Bot Framer.lottie';
import { encryptPasswordRsaOaepSha256 } from '@/utils/rsaOaepEncrypt';
import { login, fetchLoginRsaKey } from '../services/api';
import {
  setToken,
  setAccount,
  setUserRole,
  setRoleCode,
  setAllowedMenus,
  clearSessionAuth
} from '@/utils/sessionAuth';
import enabledCampusMarkerIcon from '../views/campus-enabled-marker.svg';
import disabledCampusMarkerIcon from '../views/campus-disabled-marker.svg';
import premarkCampusMarkerIcon from '../views/campus-premark-marker.svg';
import studentMarkerPersonImage from '../views/student-marker-person-watermarked.png';
import {
  MAP_RESOURCE_CACHE_KEYS,
  preloadMapStaticResources,
  preloadInitialTileSet
} from '../utils/mapResourceCache';
import { getSystemConfig } from '@/utils/systemConfig';
import { getFirstAllowedPath } from '@/utils/menuPermissions';
import { resolvePostLoginRoute } from '@/utils/manageEntryRedirect.mjs';

const LOTTIE_ARCHIVE_ENTRY = 'a/Main Scene.json';

async function extractLottieArchive(archiveUrl) {
  const response = await fetch(archiveUrl);
  if (!response.ok) {
    throw new Error(`Failed to fetch lottie archive: ${response.status}`);
  }

  return unzipSync(new Uint8Array(await response.arrayBuffer()));
}

export default {
  name: 'LoginPanel',
  data() {
    return {
      form: {
        phone: '',
        password: ''
      },
      formRules: {
        phone: [
          { required: true, message: '请输入账号或手机号', trigger: ['blur', 'change'] }
        ],
        password: [
          { required: true, message: '请输入登录密码', trigger: ['blur', 'change'] }
        ]
      },
      loading: false,
      message: '',
      systemTitle: '学途无忧后台管理系统',
      fullScreenBound: false,
      lottieInstance: null,
      lottieSourceUrl: botFramerLottieArchive
    };
  },
  mounted() {
    const config = getSystemConfig();
    this.systemTitle = config.systemTitle;
    const flash = sessionStorage.getItem('loginFlashMessage');
    if (flash) {
      this.message = flash;
      this.$message.error(flash);
      sessionStorage.removeItem('loginFlashMessage');
    }
    this.enterFullscreen();
    this.bindFullscreenFallback();
    this.initLottie();
  },
  beforeDestroy() {
    this.destroyLottie();
    this.unbindFullscreenFallback();
  },
  methods: {
    createLottieInstance(animationData, prefersReducedMotion) {
      return lottie.loadAnimation({
        container: this.$refs.lottieStage,
        renderer: 'svg',
        loop: !prefersReducedMotion,
        autoplay: !prefersReducedMotion,
        animationData,
        rendererSettings: {
          preserveAspectRatio: 'xMidYMid meet',
          clearCanvas: true
        }
      });
    },
    async initLottie() {
      if (!this.$refs.lottieStage) return;

      const prefersReducedMotion =
        typeof window !== 'undefined' &&
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      let animationData = null;
      try {
        const archiveEntries = await extractLottieArchive(this.lottieSourceUrl);
        const animationPayload = archiveEntries[LOTTIE_ARCHIVE_ENTRY];
        if (!animationPayload) {
          throw new Error('Cute Interactive Bot archive is missing its animation');
        }
        animationData = JSON.parse(strFromU8(animationPayload));
      } catch (error) {
        console.error('Failed to load Cute Interactive Bot Framer.lottie', error);
        return;
      }

      this.lottieInstance = this.createLottieInstance(animationData, prefersReducedMotion);

      if (prefersReducedMotion && this.lottieInstance) {
        this.lottieInstance.goToAndStop(128, true);
      }
    },
    destroyLottie() {
      if (this.lottieInstance) {
        this.lottieInstance.destroy();
        this.lottieInstance = null;
      }
    },
    isFullscreenActive() {
      return !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
      );
    },
    enterFullscreen() {
      if (this.isFullscreenActive()) return;
      const elem = document.documentElement;
      try {
        if (elem.requestFullscreen) {
          elem.requestFullscreen().catch(() => {});
        } else if (elem.webkitRequestFullscreen) {
          elem.webkitRequestFullscreen();
        } else if (elem.mozRequestFullScreen) {
          elem.mozRequestFullScreen();
        } else if (elem.msRequestFullscreen) {
          elem.msRequestFullscreen();
        }
      } catch (error) {
        // Ignore browser policy limits and keep login available.
      }
    },
    onFirstUserGestureEnterFullscreen() {
      this.enterFullscreen();
      if (this.isFullscreenActive()) {
        this.unbindFullscreenFallback();
      }
    },
    bindFullscreenFallback() {
      if (this.fullScreenBound) return;
      this.fullScreenBound = true;
      const handler = this.onFirstUserGestureEnterFullscreen;
      document.addEventListener('click', handler, true);
      document.addEventListener('keydown', handler, true);
      document.addEventListener('touchstart', handler, true);
    },
    unbindFullscreenFallback() {
      if (!this.fullScreenBound) return;
      const handler = this.onFirstUserGestureEnterFullscreen;
      document.removeEventListener('click', handler, true);
      document.removeEventListener('keydown', handler, true);
      document.removeEventListener('touchstart', handler, true);
      this.fullScreenBound = false;
    },
    handleSubmit() {
      this.message = '';
      if (!this.$refs.loginForm) return;

      this.$refs.loginForm.validate(async (valid) => {
        if (!valid) return;

        this.loading = true;
        try {
          const keyPayload = await fetchLoginRsaKey();
          if (!keyPayload.success || !keyPayload.data) {
            const tip = keyPayload.message || '获取登录密钥失败';
            this.message = tip;
            this.$message.error(tip);
            return;
          }

          const { keyId, publicKey } = keyPayload.data;
          let passwordCipher = '';
          try {
            passwordCipher = await encryptPasswordRsaOaepSha256(publicKey, this.form.password);
          } catch (error) {
            passwordCipher = '';
          }

          if (!passwordCipher) {
            const tip = '密码加密失败，请重试';
            this.message = tip;
            this.$message.error(tip);
            return;
          }

          const res = await login({
            phone: this.form.phone,
            keyId,
            passwordCipher
          });

          if (!res.success) {
            const tip = res.message || '登录失败';
            this.message = tip;
            this.$message.error(tip);
            return;
          }

          let menus = (res.data && res.data.user && res.data.user.menus) || [];
          if (typeof menus === 'string') {
            try {
              menus = JSON.parse(menus);
            } catch (error) {
              menus = [];
            }
          }
          if (!Array.isArray(menus)) menus = [];
          menus = menus.map((item) => String(item).trim()).filter(Boolean);

          if (!menus.length) {
            this.message = '当前账号未配置菜单权限，请联系管理员';
            clearSessionAuth();
            this.$message.error(this.message);
            return;
          }

          const account = (
            res.data &&
            res.data.user &&
            (res.data.user.nickname || res.data.user.phone)
          ) || this.form.phone;
          const token = res.data && res.data.token;
          if (token) setToken(token);
          setAccount(account);
          setAllowedMenus(menus);

          const userRole = res.data && res.data.user && res.data.user.role;
          setUserRole(userRole != null && userRole !== '' ? userRole : '');

          const roleCodeRaw = res.data && res.data.user && res.data.user.roleCode;
          const roleCode = roleCodeRaw != null && String(roleCodeRaw).trim()
            ? String(roleCodeRaw).trim()
            : (userRole != null && String(userRole).trim() ? String(userRole).trim() : '');
          setRoleCode(roleCode);

          Promise.all([
            preloadMapStaticResources([
              { cacheKey: MAP_RESOURCE_CACHE_KEYS.enabledCampusMarker, url: enabledCampusMarkerIcon },
              { cacheKey: MAP_RESOURCE_CACHE_KEYS.disabledCampusMarker, url: disabledCampusMarkerIcon },
              { cacheKey: MAP_RESOURCE_CACHE_KEYS.premarkCampusMarker, url: premarkCampusMarkerIcon },
              { cacheKey: MAP_RESOURCE_CACHE_KEYS.studentMarker, url: studentMarkerPersonImage }
            ]),
            preloadInitialTileSet()
          ]).catch(() => {});

          const firstPath = getFirstAllowedPath(menus) || '/dashboard';
          const redirectTarget = resolvePostLoginRoute(
            this.$route && this.$route.query ? this.$route.query.redirect : '',
            firstPath
          );
          this.$router.push(redirectTarget);
        } catch (error) {
          const backendMsg = (error.response && error.response.data && error.response.data.message) || '';
          const runtimeMsg = error && error.message === 'Network Error'
            ? '网络异常，请检查后台地址或网络'
            : (error && error.message) || '';
          const tip = backendMsg || runtimeMsg || '网络异常';
          this.message = tip;
          this.$message.error(tip);
        } finally {
          this.loading = false;
        }
      });
    }
  }
};
</script>

<style scoped>
.login-container {
  --login-ink: #f4f8ff;
  --login-muted: #bdcce0;
  --login-blue: #67aaff;
  --login-border: #3a5578;
  min-height: 100vh;
  min-height: 100dvh;
  background: #091d3a;
  color: var(--login-ink);
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Display", "PingFang SC", "Microsoft YaHei", sans-serif;
}

.login-shell {
  min-height: 100vh;
  min-height: 100dvh;
}

.login-showcase {
  position: relative;
  isolation: isolate;
  overflow: hidden;
  min-height: 100vh;
  min-height: 100dvh;
  min-width: 0;
  background: #091d3a;
  color: #fff;
}

.login-showcase::before,
.login-showcase::after {
  content: '';
  position: absolute;
  z-index: 0;
  pointer-events: none;
}

.login-showcase::before {
  left: -180px;
  bottom: -310px;
  width: 660px;
  height: 660px;
  border: 1px solid rgba(144, 188, 244, 0.17);
  border-radius: 50%;
  background: radial-gradient(circle, rgba(43, 97, 160, 0.14), transparent 68%);
}

.login-showcase::after {
  top: 54px;
  right: 54px;
  width: 154px;
  height: 154px;
  background: url('../assets/login-diagonal-dots.svg') center / contain no-repeat;
}

.showcase-content {
  position: relative;
  z-index: 1;
  box-sizing: border-box;
  width: min(100%, 1400px);
  min-height: 100vh;
  min-height: 100dvh;
  margin: 0 auto;
  padding: clamp(32px, 4vw, 64px) clamp(32px, 6vw, 96px) 28px;
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(380px, 0.9fr);
  grid-template-rows: auto auto minmax(0, 1fr) auto;
  column-gap: clamp(64px, 8vw, 140px);
}

.showcase-brand {
  grid-column: 1;
  grid-row: 1;
  display: flex;
  align-items: center;
  gap: 12px;
  color: #f1f6ff;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.02em;
}

.showcase-mark {
  width: 38px;
  height: 38px;
  display: grid;
  place-items: center;
  border: 1px solid rgba(255, 255, 255, 0.45);
  border-radius: 11px;
  font-size: 20px;
}

.showcase-heading {
  grid-column: 1;
  grid-row: 2;
  margin-top: clamp(48px, 7vh, 80px);
}

.showcase-overline {
  margin: 20px;
  color: #a9c9f4;
  font-size: 15px;
  font-weight: 500;
  letter-spacing: 0.03em;
}

.showcase-heading h1 {
  max-width: 10em;
  margin: 0;
  color: #fff;
  font-size: clamp(38px, 3.9vw, 60px);
  font-weight: 650;
  letter-spacing: -0.035em;
  line-height: 1.18;
  text-wrap: balance;
}

.showcase-description {
  max-width: 32em;
  margin: 22px 0 0;
  color: #c1cfe1;
  font-size: 16px;
  line-height: 1.8;
}

.showcase-login-heading,
.login-form {
  width: 100%;
  max-width: 420px;
  min-width: 0;
}

.showcase-login-heading {
  grid-column: 2;
  grid-row: 2;
  align-self: end;
  margin-bottom: 28px;
}

.showcase-login-heading h2 {
  margin: 0;
  color: #f4f8ff;
  font-size: clamp(34px, 3vw, 44px);
  font-weight: 650;
  letter-spacing: -0.035em;
  line-height: 1.2;
}

.login-form {
  grid-column: 2;
  grid-row: 3;
  align-self: start;
}

.login-form :deep(.el-form-item) {
  margin-bottom: 25px;
}

.login-form :deep(.el-form-item__label) {
  padding-bottom: 10px;
  color: #e2ebf8;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.4;
}

.login-form :deep(.el-input__inner) {
  height: 52px;
  line-height: 52px;
  border: 1px solid var(--login-border);
  border-radius: 8px;
  background: #102b4d;
  color: #f4f8ff;
  font-size: 16px;
  transition: border-color 200ms ease, background-color 200ms ease, box-shadow 200ms ease;
}

.login-form :deep(.el-input__inner::placeholder) {
  color: #bac9db;
}

.login-form :deep(.el-input__inner:hover) {
  border-color: #7e9dc3;
}

.login-form :deep(.el-input__inner:focus) {
  border-color: var(--login-blue);
  background: #143457;
  box-shadow: 0 0 0 3px rgba(103, 170, 255, 0.18);
}

.login-form :deep(.el-input__prefix),
.login-form :deep(.el-input__suffix) {
  color: #b4c8e1;
}

.login-form :deep(.el-form-item__error) {
  padding-top: 6px;
  color: #ffaaa7;
}

.login-form :deep(.login-form__action) {
  margin-top: 34px;
  margin-bottom: 0;
}

.login-btn {
  width: 100%;
  height: 52px;
  border: 1px solid #7db5ff;
  border-radius: 8px;
  background: #7db5ff;
  color: #071b34;
  font-size: 16px;
  font-weight: 600;
  transition: background-color 200ms ease, border-color 200ms ease, box-shadow 200ms ease;
}

.login-btn:hover,
.login-btn:focus {
  border-color: #a4cdff;
  background: #a4cdff;
}

.login-btn:active {
  border-color: #65a4f5;
  background: #65a4f5;
}

.msg {
  margin: 18px 0 0;
  color: #ffaaa7;
  font-size: 14px;
  line-height: 1.5;
}

.footer-content {
  grid-column: 1 / -1;
  grid-row: 4;
  align-self: end;
  width: 100%;
  margin-top: 48px;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px 32px;
  color: #a9bdd7;
  font-size: 11px;
  line-height: 1.7;
}

.footer-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 18px;
}

.footer-copyright {
  text-align: right;
}

.footer-link {
  color: inherit;
  text-decoration: none;
}

.footer-link:hover,
.footer-link:focus-visible {
  color: #d7e8ff;
  text-decoration: underline;
}

@media (max-width: 900px) {
  .showcase-content {
    width: min(100%, 660px);
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: none;
    padding: 28px 32px 30px;
  }

  .showcase-brand,
  .showcase-heading,
  .showcase-login-heading,
  .login-form,
  .footer-content {
    grid-column: 1;
    grid-row: auto;
  }

  .showcase-heading {
    margin-top: 48px;
  }

  .showcase-heading h1 {
    font-size: 42px;
  }

  .showcase-login-heading {
    margin: 24px 0 28px;
  }

  .footer-content {
    margin-top: 48px;
    flex-direction: column;
    align-items: flex-start;
  }

  .footer-copyright {
    text-align: left;
  }
}

@media (max-width: 600px) {
  .login-showcase::before {
    left: -220px;
    bottom: -250px;
    width: 460px;
    height: 460px;
  }

  .login-showcase::after {
    top: 20px;
    right: -30px;
    width: 110px;
    height: 110px;
  }

  .showcase-content {
    padding: 24px 24px 18px;
  }

  .showcase-heading {
    margin-top: 36px;
  }

  .showcase-overline {
    margin-bottom: 12px;
    font-size: 13px;
  }

  .showcase-heading h1 {
    font-size: 31px;
  }

  .showcase-description {
    margin-top: 12px;
    font-size: 14px;
  }

  .showcase-login-heading {
    margin: 20px 0 28px;
  }

  .showcase-login-heading h2 {
    font-size: 34px;
  }

  .footer-content {
    margin-top: 56px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .login-form :deep(.el-input__inner),
  .login-btn {
    transition: none;
  }
}
</style>
